import { Button, Loading, Title } from "@components";

import styles from "styles/pages/reports/reports.module.scss";

import OrdersInPeriod from "./OrdersInPeriod";
import OrdersDistribution from "./OrdersDistribution";
import { Currency } from "shared/utils/Currency";
import Filters from "../Filters";
import { useEffect, useRef, useState } from "react";
import ReportFilters from "../ReportFilters";
import { If } from "shared/components/control-statements";
import { ReportsAPI } from "providers/ReportsAPI";
import { Numbers } from "shared/utils/Numbers";
import { OlgaEnvironmentsAPI } from "providers/OlgaEnvironmentsAPI";
import { Trend } from "../Reports";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";

import Recurrency from "./Recurrency";
import { Environment } from "shared/core/Environment";

dayjs.locale("pt-br");

const getPeriodCompare = ({ dateFilters }) => {
  if (dateFilters) return "";

  const today = dayjs();
  const startDate = today.startOf("month");
  const endDate = today;

  const startLastMonth = startDate.subtract(1, "month");
  const endLastMonth = startLastMonth.add(endDate.date() - 1, "day");

  const formattedPeriod = `Comparado a ${startLastMonth.date()} a ${endLastMonth.date()} de ${endLastMonth.format(
    "MMMM"
  )}`;

  return formattedPeriod;
};

const BigNumber = ({ data, dateFilters, hasReportFilters }) => {
  return (
    <div className={styles.bigNumber}>
      <div className="d-flex flex-column">
        <div className="d-flex align-center">
          <p className={styles.number}>{data.value}</p>
          <If cond={!hasReportFilters}>
            <Trend
              growth={data.growth}
              tooltip={getPeriodCompare(dateFilters)}
            />
          </If>
        </div>
        <div className={styles.title}>
          <p>{data.title}</p>
          <If cond={data.tooltip}>
            <span className={styles.info} title={data.tooltip}>
              <i className="fal fa-info-circle" />
            </span>
          </If>
        </div>
      </div>
    </div>
  );
};

const numbers = (data = {}) => {
  return [
    {
      title: "Faturamento bruto",
      value: Currency.formatCurrency(data?.order_total?.value || 0),
      growth: data?.order_total?.growth || 0,
      tooltip: "Valor de todos os pedidos somados.",
    },
    {
      title: "Taxa de frete",
      value: Currency.formatCurrency(data?.delivery_total?.value || 0),
      growth: data?.delivery_total?.growth || 0,
      tooltip: "Valor de todas as taxas de frete somadas.",
    },
    {
      title: "Taxa de serviço",
      value: Currency.formatCurrency(data?.service_total?.value || 0),
      growth: data?.service_total?.growth || 0,
      tooltip: "Valor de todos as taxas de serviço somadas.",
    },
    {
      title: "Faturamento líquido",
      value: Currency.formatCurrency(
        data?.total_without_delivery_and_service?.value || 0
      ),
      growth: data?.total_without_delivery_and_service?.growth || 0,
      tooltip: "Valor de todos os pedidos somados, desconsiderando taxas.",
    },
    {
      title: "Quantidade de pedidos",
      value: Numbers.formatInt(data?.order_quantity?.value || 0),
      growth: data?.order_quantity?.growth || 0,
    },
    {
      title: "Ticket médio",
      value: Currency.formatCurrency(data?.average_order_value?.value || 0),
      growth: data?.average_order_value?.growth || 0,
      tooltip:
        "Valor de todos os pedidos somados divididos pela quantidade de pedidos.",
    },
    {
      title: "Pedidos por cliente",
      value: Numbers.formatFloat(data?.orders_per_client?.value || 0, 2),
      growth: data?.orders_per_client?.growth || 0,
      tooltip: "Quantidade média de pedidos por cliente identificado.",
    },
    {
      title: "Produtos por pedido",
      value: Numbers.formatFloat(data?.products_per_order?.value || 0, 2),
      growth: data?.products_per_order?.growth || 0,
      tooltip:
        "Quantidade de produtos pedidos divididos pela quantidade de pedidos.",
    },
  ];
};

export default function OrderReport() {
  const [openFilters, setOpenFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const [dateFilters, setDateFilters] = useState({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [label_dict, setLabelDict] = useState(new Map());

  const filtersRef = useRef(null);

  const fetchData = async () => {
    setLoading(true);
    const provider = new ReportsAPI();
    const environment = Environment.configurations;

    const reports_allow_origin = !environment.reports_allow_origin;

    const response = await provider.orders({
      ...filters,
      block_origin: reports_allow_origin,
    });
    setData(response);
    setLoading(false);
  };

  useEffect(() => {
    fetchUnits();
    fetchData();
  }, [filters]);

  const fetchUnits = async () => {
    const response_units = await OlgaEnvironmentsAPI.getUnits();
    const response_brands = await OlgaEnvironmentsAPI.getBrands();

    const units = response_units.getData([]);
    const brands = response_brands.getData([]);

    setLabelDict(
      new Map([
        ...units.map((unit) => [`units_${unit.id}`, unit.name]),
        ...brands.map((brand) => [`brands_${brand.id}`, brand.name]),
      ])
    );
  };

  const isSameMonth = dayjs.utc(filters?.start_date).isSame(dayjs(), "month");
  const hasReportFilters =
    (filters?.period &&
      filters?.period !== "FROM_THE_BEGINNING" &&
      filters?.period !== "SPECIFIC_MONTH") ||
    (filters?.period === "SPECIFIC_MONTH" && !isSameMonth) ||
    filters.unit_id?.length > 0 ||
    filters.origin?.length > 0;

  return (
    <>
      <Title title="Relatório de Pedidos">
        <Filters
          onRemoveFilter={(filter) => {
            filtersRef.current?.removeFilter(filter);
          }}
          filters={filters}
          units={label_dict}
        />
        <Button onClick={() => setOpenFilters(true)}>Filtrar</Button>
      </Title>

      <If cond={loading}>
        <Loading fullContent />
      </If>

      <If cond={!loading}>
        <div className={styles.bigNumbersOrders}>
          {numbers(data?.statistics || {}).map((item) => (
            <BigNumber
              key={item.title}
              data={item}
              dateFilters={dateFilters}
              hasReportFilters={hasReportFilters}
            />
          ))}
        </div>

        <OrdersInPeriod
          data={data?.periods || []}
          hasReportFilters={hasReportFilters}
        />
        <OrdersDistribution
          data={data?.distribution || {}}
          hasReportFilters={hasReportFilters}
        />
        <Recurrency
          data={data?.recurrency || []}
          hasReportFilters={hasReportFilters}
        />
        {/* <Registers data={data?.customers || []} /> */}

        <ReportFilters
          ref={filtersRef}
          open={openFilters}
          filters={filters}
          dateFiltersProp={dateFilters}
          onClose={() => setOpenFilters(false)}
          onUpdateFilters={(filters) => {
            setFilters(filters);
          }}
          ignoreBlockOrigins={false}
          onUpdateDateFilters={(filters) => {
            setDateFilters(filters);
          }}
        />
      </If>
    </>
  );
}
